import { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Grid,
  Stack,
  Box,
  CircularProgress,
  Typography,
  TextField,
  MenuItem,
  Tooltip,
  IconButton,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Snackbar,
  Alert,
  useTheme,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { Calendar, MapPin, Pencil, Info, ShoppingCart, Check } from "lucide-react";

import { useTranslation } from "../../../language";
import * as c from "../../../constants";
import ButtonForm from "../../../components/Button/ButtonForm";
import FormAlert from "../../../components/Alert/FormAlert";
import CustomDialogSmall from "../../../components/Dialog/DiaCompSmall";
import CustomDialog from "../../../components/Dialog/DiaCompMedium";
import DiaLabDetails from "../../Onboarding/WaitingResults/DiaLabDetails";

const PREP_GUIDELINES = [
  { labelKey: "labvisitstep_prep_guideline_fasting_label", textKey: "labvisitstep_prep_guideline_fasting_text" },
  { labelKey: "labvisitstep_prep_guideline_hydration_label", textKey: "labvisitstep_prep_guideline_hydration_text" },
  { labelKey: "labvisitstep_prep_guideline_alcohol_label", textKey: "labvisitstep_prep_guideline_alcohol_text" },
  { labelKey: "labvisitstep_prep_guideline_morning_label", textKey: "labvisitstep_prep_guideline_morning_text" },
  { labelKey: "labvisitstep_prep_guideline_medication_label", textKey: "labvisitstep_prep_guideline_medication_text" },
  { labelKey: "labvisitstep_prep_guideline_supplements_label", textKey: "labvisitstep_prep_guideline_supplements_text" },
  { labelKey: "labvisitstep_prep_guideline_after_label", textKey: "labvisitstep_prep_guideline_after_text" },
];

function cardSx(theme, accentColor) {
  return {
    border: '1px solid',
    borderRadius: 4,
    overflow: 'hidden',
    borderLeft: `10px solid ${accentColor}`,
    ...(theme.palette.mode === 'light'
      ? { bgcolor: 'grey.C50', borderColor: 'grey.C900' }
      : { bgcolor: 'greydark.C900', borderColor: 'greydark.C400',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }),
  };
}

const DATE_FORMAT = "YYYY-MM-DD";
const TIME_FORMAT = "HH:mm";

// Themed pickers — MUI's DatePicker/TimePicker popups can be recolored for dark mode,
// unlike a native <input type="date|time">
function ThemedDateInput({ label, value, onChange, error, helperText }) {
  const theme = useTheme();
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label={label}
        value={value ? dayjs(value, DATE_FORMAT) : null}
        onChange={(newValue) =>
          onChange(newValue && newValue.isValid() ? newValue.format(DATE_FORMAT) : "")
        }
        format={DATE_FORMAT}
        slotProps={{
          textField: { fullWidth: true, size: "small", error, helperText },
          popper: {
            sx: {
              '& .MuiPaper-root': { backgroundColor: theme.palette.grey.C200, borderRadius: 6 },
              '& .MuiPickersCalendarHeader-root': { backgroundColor: theme.palette.grey.C300 },
              '& .MuiDayCalendar-weekDayLabel': { color: theme.palette.common.black, fontWeight: 600 },
              '& .MuiPickersDay-root': { color: theme.palette.greydark.C900, fontWeight: 600 },
              '& .MuiPickersDay-root.Mui-selected': { backgroundColor: theme.palette.grey.C500 },
              '& .MuiPickersYear-yearButton.Mui-selected': { backgroundColor: theme.palette.greydark.C900 },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
}

function ThemedTimeInput({ label, value, onChange, error, helperText }) {
  const theme = useTheme();
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TimePicker
        label={label}
        ampm={false}
        value={value ? dayjs(value, TIME_FORMAT) : null}
        onChange={(newValue) =>
          onChange(newValue && newValue.isValid() ? newValue.format(TIME_FORMAT) : "")
        }
        slotProps={{
          textField: { fullWidth: true, size: "small", error, helperText },
          popper: {
            sx: {
              '& .MuiPaper-root': { backgroundColor: theme.palette.grey.C200, borderRadius: 6 },
              '& .MuiClockNumber-root': { color: theme.palette.greydark.C900, fontWeight: 600 },
              '& .MuiClockNumber-root.Mui-selected': { color: theme.palette.common.white },
              '& .MuiClock-pin, & .MuiClockPointer-root': { backgroundColor: theme.palette.grey.C500 },
              '& .MuiClockPointer-thumb': { backgroundColor: theme.palette.grey.C500, borderColor: theme.palette.grey.C500 },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
}

// Shared "administer this lab visit" card — visit summary, reschedule/prep/tests
// dialogs, and the confirm-visit action. Used by both LabvisitStepAdmin (standard
// path, state 4 → confirmState 5) and MicrobiomePath (gut-microbiome path, state 41
// → confirmState 42). The caller owns what state to confirm into and what happens
// next via `onConfirmed` — this component never navigates itself.
export default function LabVisitAdminConfirmCard({ labVisitId, confirmState, instructionsText, onConfirmed, maxWidth = 600 }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const [visit, setVisit] = useState(null);
  const [visitLoading, setVisitLoading] = useState(true);

  const [labs, setLabs] = useState([]);
  const [labsLoading, setLabsLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [editLaboratoryId, setEditLaboratoryId] = useState("");
  const [editVisitDate, setEditVisitDate] = useState("");
  const [editVisitTime, setEditVisitTime] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editFieldErrors, setEditFieldErrors] = useState({});
  const [editError, setEditError] = useState(null);
  const [editSuccess, setEditSuccess] = useState(false);

  const [prepOpen, setPrepOpen] = useState(false);

  const [addonOrder, setAddonOrder] = useState(null);
  const [addonOrderLoading, setAddonOrderLoading] = useState(true);
  const [testsOpen, setTestsOpen] = useState(false);

  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState(null);

  // ── 1. Fetch the visit ────────────────────────────────────────────────────
  useEffect(() => {
    if (!labVisitId) return;
    let mounted = true;

    const fetchVisit = async () => {
      setVisitLoading(true);
      try {
        const { data } = await axios.get(`${c.API_URL}/api/user-lab-visits/${labVisitId}/`);
        if (mounted) setVisit(data ?? null);
      } catch (err) {
        console.error("Failed to fetch lab visit:", err);
      } finally {
        if (mounted) setVisitLoading(false);
      }
    };

    fetchVisit();
    return () => { mounted = false; };
  }, [labVisitId]);

  // ── 2. Fetch laboratory options ──────────────────────────────────────────
  useEffect(() => {
    const fetchLabs = async () => {
      try {
        const { data } = await axios.get(`${c.API_URL}/api/laboratories/`);
        setLabs(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch labs:", err);
      } finally {
        setLabsLoading(false);
      }
    };
    fetchLabs();
  }, []);

  // ── 3. Fetch ordered add-ons for this visit ──────────────────────────────
  useEffect(() => {
    if (!labVisitId) return;

    const fetchOrder = async () => {
      try {
        const { data } = await axios.get(`${c.API_URL}/api/orders/by-lab-visit/${labVisitId}`);
        setAddonOrder(data ?? null);
      } catch (err) {
        console.error("No add-on order items found:", err);
      } finally {
        setAddonOrderLoading(false);
      }
    };

    fetchOrder();
  }, [labVisitId]);

  const handleConfirmVisit = async () => {
    setCompleting(true);
    setCompleteError(null);
    try {
      await axios.patch(`${c.API_URL}/api/user-lab-visits/${labVisitId}/`, {
        state: confirmState,
      });
      onConfirmed?.();
    } catch (err) {
      console.error("Failed to confirm lab visit:", err);
      setCompleteError(t("val_server_error"));
      setCompleting(false);
    }
  };

  const handleOpenEdit = () => {
    setEditLaboratoryId(visit?.laboratory?.id ?? "");
    setEditVisitDate(visit?.visit_date ?? "");
    setEditVisitTime(visit?.visit_time ? visit.visit_time.slice(0, 5) : "");
    setEditFieldErrors({});
    setEditError(null);
    setEditOpen(true);
  };

  // PATCHes the specific visit record by ID — repeat customers can have several
  // visit records, so this must be ID-targeted rather than an implicit "current visit".
  const handleSaveSchedule = async () => {
    setEditSaving(true);
    setEditFieldErrors({});
    setEditError(null);
    try {
      await axios.patch(`${c.API_URL}/api/user-lab-visits/${labVisitId}/`, {
        laboratory: editLaboratoryId,
        visit_date: editVisitDate,
        visit_time: editVisitTime,
      });
      const lab = labs.find((l) => String(l.id) === String(editLaboratoryId));
      setVisit((prev) =>
        prev ? { ...prev, laboratory: lab ?? prev.laboratory, visit_date: editVisitDate, visit_time: editVisitTime } : prev
      );
      setEditOpen(false);
      setEditSuccess(true);
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data) {
        setEditFieldErrors(err.response.data);
      } else {
        setEditError(t("val_server_error"));
      }
    } finally {
      setEditSaving(false);
    }
  };

  const accentColor = theme.palette.tertiary.C300;
  const labDetails = visit?.laboratory ?? null;
  const hasOrderItems = addonOrder?.items?.length > 0;

  if (visitLoading) {
    return (
      <Grid container sx={{ justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Grid>
    );
  }

  if (!visit) return null;

  return (
    <>
      <Box sx={{ width: '100%', maxWidth, mx: 'auto' }}>
        <Box sx={{ ...cardSx(theme, accentColor), p: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
                bgcolor: theme.palette.mode === 'light' ? 'grey.C100' : 'greydark.C900',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <MapPin size={20} color={accentColor} />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={400}>
                {visit.laboratory?.name ?? "—"}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Calendar size={16} color={accentColor} />
                <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                  {t("onb_visit_date_label")} {visit.visit_date ?? "—"} · {t("onb_visit_time_label")}{" "}
                  {visit.visit_time ? visit.visit_time.slice(0, 5) : "—"}
                </Typography>
              </Stack>
            </Box>
            <DiaLabDetails labDetails={labDetails} />
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
            <Typography variant="body2">
              {t("labvisitstep_change_prompt")}
            </Typography>
            <Tooltip title={t("labvisitstep_change_dialog_title")}>
              <IconButton size="small" onClick={handleOpenEdit}>
                <Pencil size={20} color={theme.palette.primary.main} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
            <Typography variant="body2">
              {t("labvisitstep_prep_title")}
            </Typography>
            <Tooltip title={t("labvisitstep_prep_title")}>
              <IconButton size="small" onClick={() => setPrepOpen(true)}>
                <Info size={20} color={theme.palette.primary.main} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
            <Typography variant="body2">
              {t("labvisitstep_tests_prompt")}
            </Typography>
            <Tooltip title={t("labvisitstep_tests_dialog_title")}>
              <IconButton size="small" onClick={() => setTestsOpen(true)}>
                <ShoppingCart size={20} color={theme.palette.primary.main} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {instructionsText && (
          <Typography variant="subtitle2" textAlign="center" sx={{ fontWeight: 400, mt: 4 }}>
            {instructionsText}
          </Typography>
        )}

        <Grid container sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <ButtonForm
            name={completing ? <CircularProgress size={18} color="inherit" /> : t("labvisitstep_complete_button")}
            onClick={handleConfirmVisit}
            disabled={completing}
          />
          {completeError && <FormAlert severity="error" message={completeError} sx={{ mt: 2 }} />}
        </Grid>
      </Box>

      <CustomDialogSmall
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={t("labvisitstep_change_dialog_title")}
        content={
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {labsLoading ? (
              <Grid container sx={{ justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Grid>
            ) : (
              <TextField
                select
                size="small"
                fullWidth
                label={t("onb_lab5")}
                value={editLaboratoryId}
                onChange={(e) => setEditLaboratoryId(e.target.value)}
                error={Boolean(editFieldErrors.laboratory)}
                helperText={editFieldErrors.laboratory?.[0]}
              >
                {labs.map((lab) => (
                  <MenuItem key={lab.id} value={lab.id}>{lab.name}</MenuItem>
                ))}
              </TextField>
            )}

            <ThemedDateInput
              label={t("onb_lab3")}
              value={editVisitDate}
              onChange={setEditVisitDate}
              error={Boolean(editFieldErrors.visit_date)}
              helperText={editFieldErrors.visit_date?.[0]}
            />

            <ThemedTimeInput
              label={t("onb_lab4")}
              value={editVisitTime}
              onChange={setEditVisitTime}
              error={Boolean(editFieldErrors.visit_time)}
              helperText={editFieldErrors.visit_time?.[0]}
            />

            {editError && <FormAlert severity="error" message={editError} />}

            <ButtonForm
              name={editSaving ? <CircularProgress size={18} color="inherit" /> : t("onb_labb")}
              onClick={handleSaveSchedule}
              disabled={editSaving || !editLaboratoryId || !editVisitDate || !editVisitTime}
            />
          </Stack>
        }
      />

      <CustomDialog
        open={prepOpen}
        onClose={() => setPrepOpen(false)}
        title={t("labvisitstep_prep_title")}
        content={
          <Stack spacing={2.5}>
            <Typography variant="body2">
              <Typography component="span" fontWeight={700}>{t("labvisitstep_prep_important_label")}</Typography>{" "}
              {t("labvisitstep_prep_important_text")}
            </Typography>

            <Typography variant="body2">
              <Typography component="span" fontWeight={700}>{t("labvisitstep_prep_health_label")}</Typography>{" "}
              {t("labvisitstep_prep_health_text")}
            </Typography>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                {t("labvisitstep_prep_whathappens_heading")}
              </Typography>
              <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>{t("labvisitstep_prep_whathappens_item1")}</Typography>
                <Typography component="li" variant="body2">{t("labvisitstep_prep_whathappens_item2")}</Typography>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                {t("labvisitstep_prep_prep_heading")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t("labvisitstep_prep_prep_lead")}
              </Typography>
              <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                {PREP_GUIDELINES.map(({ labelKey, textKey }) => (
                  <Typography key={labelKey} component="li" variant="body2" sx={{ mb: 0.5 }}>
                    <Typography component="span" fontWeight={700}>{t(labelKey)}</Typography>: {t(textKey)}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Stack>
        }
      />

      <CustomDialog
        open={testsOpen}
        onClose={() => setTestsOpen(false)}
        title={t("labvisitstep_tests_dialog_title")}
        content={
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Check size={20} strokeWidth={2.5} color={theme.palette.traffic.green} />
              <Typography variant="body2" fontWeight={600}>
                {t("labvisitstep_tests_core_label")}
              </Typography>
            </Stack>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                {t("onb_subscr32")}
              </Typography>

              {addonOrderLoading ? (
                <Grid container sx={{ justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Grid>
              ) : hasOrderItems ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("labvisitstep_tests_table_name")}</TableCell>
                      <TableCell align="right">{t("labvisitstep_tests_table_price")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {addonOrder.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.product_name?.name ?? t("onb_wait_unnamed_test")}</TableCell>
                        <TableCell align="right">€&nbsp;{(item.unit_price_cents / 100).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("onb_wait_no_addons")}
                </Typography>
              )}
            </Box>
          </Stack>
        }
      />

      <Snackbar
        open={editSuccess}
        autoHideDuration={3000}
        onClose={() => setEditSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setEditSuccess(false)}>
          {t("labvisitstep_change_success")}
        </Alert>
      </Snackbar>
    </>
  );
}
